import { healMilestonePaymentOverridesInDesign } from 'projectSections/sections/payments/milestonePaymentOverrideUtils'
import { fetchRequest } from 'util/fetch'

import {
  applyFreshUserDataToStudioSystem,
  injectQuoteOsSystemIntoEditor,
  normalizeDesignJsonForStudioLoad,
  parseDesignFromProjectPayload,
  resolveQuoteStudioSystemFromDesign,
  setQuoteProjectSnapshot,
} from './quoteDesignSnapshot'

const normUuid = (u: string | null | undefined) => (u || '').toLowerCase()

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * Fetch the project once and cache it so `useQuoteStudioSystem` can read the latest design
 * without going through Studio. Returns the parsed `userData.payment_options.length`.
 */
export async function refreshQuoteProjectSnapshot(params: {
  orgId: number | string
  projectId: string | number
  systemUuid: string
}): Promise<number> {
  const w = window as any
  const { orgId, projectId, systemUuid } = params

  const requestUrl = `${w.API_ROOT}/api/orgs/${orgId}/projects/${projectId}/`
  const headers = w.Utils.tokenAuthHeaders({
    'Content-Type': 'application/json',
    'X-CSRFToken': w.getCookie('csrftoken'),
  })
  const request = new Request(requestUrl, { method: 'GET', headers: new Headers(headers) })
  const { json: projectData } = await fetchRequest(request, { format: 'json' })

  setQuoteProjectSnapshot(projectId, projectData)

  const designJson = parseDesignFromProjectPayload(projectData)
  const stub = designJson ? resolveQuoteStudioSystemFromDesign(designJson, systemUuid) : null
  return stub?.payment_options?.length ?? 0
}

/**
 * Quote tab can mount before `DesignerComponent` finishes wiring `window.editor`, and — more
 * importantly — before the project shell finishes loading the design. The shell fetches the
 * project asynchronously and then calls `editor.loadScene`, which synchronously runs
 * `editor.clear()` (Editor.loadScene → parseSceneData). If a caller creates a placeholder
 * `OsSystem` (or the pro makes an unsaved upfront / line-item edit) during that window, the
 * subsequent `clear()` wipes it.
 *
 * So we wait for the editor API to exist AND for the shell's initial design load to settle:
 *  - `WorkspaceHelper.designIsLoading === false` (shell's project fetch + load chain finished), and
 *  - `editor.sceneIsLoading === false` (loadScene's async view phase finished).
 *
 * On timeout we still return `true` if the API is present so callers keep their existing
 * best-effort fallback rather than hanging.
 */
export async function waitForStudioReady(maxWaitMs = 15000): Promise<boolean> {
  const start = Date.now()
  while (Date.now() - start < maxWaitMs) {
    const w = window as any
    const editor = w.editor
    const apiReady = editor?.loadScene && w.WorkspaceHelper?.setParamsFromProjectData
    if (apiReady) {
      const shellLoading = w.WorkspaceHelper?.designIsLoading === true
      const sceneLoading = editor.sceneIsLoading === true
      if (!shellLoading && !sceneLoading) {
        return true
      }
    }
    await sleep(100)
  }
  // Timed out waiting for the scene to settle — proceed if the API is at least present so callers
  // fall back to their own recovery (placeholder creation / design-snapshot fallback).
  const w = window as any
  return !!(w.editor?.loadScene && w.WorkspaceHelper?.setParamsFromProjectData)
}

/** One in-flight sync per org+project so multiple `useEnsureQuoteSystem` mounts share a single `loadScene`. */
const syncInFlight = new Map<string, Promise<void>>()

export const studioHasSystem = (systemUuid: string) => {
  const w = window as any
  const editor = w.editor
  return !!editor?.getSystems?.()?.some((s: { uuid?: string }) => normUuid(s?.uuid) === normUuid(systemUuid))
}

/**
 * True when Studio's in-memory quote system already carries payment options. Used to decide
 * whether the server design should be rebound over it: once the live system has payment options
 * it is the source of truth (and may hold unsaved edits such as an upfront-payment override made
 * on the Payments tab), so it must not be overwritten by a freshly-fetched server payload.
 */
export const liveQuoteSystemHasPaymentOptions = (systemUuid: string): boolean => {
  const w = window as any
  const editor = w.editor
  const studioSystem = editor
    ?.getSystems?.()
    ?.find((s: { uuid?: string }) => normUuid(s?.uuid) === normUuid(systemUuid))
  return Array.isArray(studioSystem?.payment_options) && studioSystem.payment_options.length > 0
}

/**
 * After `ensure_quote_system`, Django may have written a fresh `OsSystem` stub into
 * `project.design` on the server. Studio only hydrates from the project JSON that was fetched
 * when the project shell first loaded, so `window.editor.getSystems()` can miss that node until
 * we pull the latest `design` and re-run the same `loadScene` path as `projectSections/index.tsx`.
 *
 * This is a no-op when Studio already exposes a system whose `uuid` matches (case-insensitive).
 */
export function syncQuoteSystemDesignIntoStudio(params: {
  orgId: number | string
  projectId: string | number
  systemUuid: string
}): Promise<void> {
  const key = `${params.orgId}:${params.projectId}`
  const existing = syncInFlight.get(key)
  if (existing) return existing

  const promise = (async () => {
    try {
      await runSyncOnce(params)
    } finally {
      syncInFlight.delete(key)
    }
  })()

  syncInFlight.set(key, promise)
  return promise
}

/**
 * Load a project payload's `design` into Studio using the same deserialize → normalize → loadScene
 * sequence as the project shell's `loadDesignFromProjectData` (`projectSections/index.tsx`). Kept as
 * a single named helper so the Quote sync does not hand-roll its own copy of that orchestration.
 *
 * Returns the `loadScene` logs (or `undefined` when there is no design to load). Throws on a
 * `loadScene` failure so the caller can run its own recovery (e.g. inject a quote OsSystem stub).
 *
 * NOTE: intentionally omits the shell-only side effects (segen-pricing flag, restart handler,
 * recoverable/non-recoverable error UI) — those stay in the shell's React-bound loader.
 */
export async function loadQuoteDesignIntoStudio(projectData: unknown): Promise<{ errors?: unknown[] } | undefined> {
  const w = window as any
  const editor = w.editor
  const WorkspaceHelper = w.WorkspaceHelper

  const data: any = { ...(projectData as any) }
  data.design = parseDesignFromProjectPayload(projectData)

  if (data.design?.object) {
    const systems = data.design.object?.children?.filter((child: { type?: string }) => child.type === 'OsSystem')
    systems?.forEach((system: { userData?: unknown }) => {
      if (system.userData) {
        WorkspaceHelper.refreshPaymentOptionWarnings(system.userData)
      }
    })
  }

  let initData: unknown = undefined
  if (data.design?.init) {
    initData = data.design.init
    data.design = null
  }

  if (!data || !data.design) {
    WorkspaceHelper.onLoaded(editor, false, data, initData, { autoCreateViews: true })
    return undefined
  }

  WorkspaceHelper.setParamsFromProjectData(data)

  const sceneForLoad = normalizeDesignJsonForStudioLoad(data.design)
  // Legacy overrides crash Payments before Adjust Payments can repair them (GS-9851).
  healMilestonePaymentOverridesInDesign(sceneForLoad)
  const logs = await editor.loadScene(sceneForLoad, WorkspaceHelper.params)
  WorkspaceHelper.onLoaded(editor, true, data)
  if (w.Designer?.scheduleRefreshStrings) {
    w.Designer.scheduleRefreshStrings()
  }
  return logs
}

async function runSyncOnce(params: {
  orgId: number | string
  projectId: string | number
  systemUuid: string
}): Promise<void> {
  const w = window as any
  const { orgId, projectId, systemUuid } = params

  const requestUrl = `${w.API_ROOT}/api/orgs/${orgId}/projects/${projectId}/`
  const headers = w.Utils.tokenAuthHeaders({
    'Content-Type': 'application/json',
    'X-CSRFToken': w.getCookie('csrftoken'),
  })
  const request = new Request(requestUrl, { method: 'GET', headers: new Headers(headers) })
  const { json: projectData } = await fetchRequest(request, { format: 'json' })

  setQuoteProjectSnapshot(projectId, projectData)

  // Studio may already expose the placeholder OsSystem from project bootstrap while its
  // in-memory payment_options / pricing are still the pre-quote stub. Rebind server userData
  // instead of skipping sync entirely (Payments + Quote read `editor.getSystems()`).
  //
  // BUT only when the in-memory system has no payment options yet. Once it does, it is the live,
  // possibly-edited quote system — e.g. an unsaved upfront-payment override set on the Payments tab
  // (`payment_options_settings_overrides`). A wholesale `userData` replace from the freshly-fetched
  // server design would drop those unsaved edits and revert the deposit to its default. So we treat
  // the in-memory system as the source of truth once it carries payment options, matching the
  // `ensure_quote_system` intent of only pulling from the server when we don't already have them.
  if (studioHasSystem(systemUuid)) {
    if (!liveQuoteSystemHasPaymentOptions(systemUuid)) {
      applyFreshUserDataToStudioSystem(projectData, systemUuid)
    }
    return
  }

  const studioReady = await waitForStudioReady()
  const editor = w.editor
  const WorkspaceHelper = w.WorkspaceHelper
  if (!studioReady || !editor?.loadScene || !WorkspaceHelper) {
    console.warn(
      '[syncQuoteSystemDesignIntoStudio] Studio not ready after wait — Quote widgets will use design JSON fallback if available'
    )
    return
  }

  try {
    const logs = await loadQuoteDesignIntoStudio(projectData)
    if (logs?.errors?.length) {
      console.warn('[syncQuoteSystemDesignIntoStudio] loadScene reported errors', logs.errors)
    }

    // Do not touch react-final-form's `design` field here. A previous implementation called
    // ``form.change('design', editor.serializeDesign())``, which marked `design` dirty; the next
    // Save (e.g. Valid Until) then ran ``formatDesignSubmitValues`` and PATCHed
    // ``window.editor.sceneAsJSON()``, which could wipe Quote ``userData.line_items`` if Studio
    // was still on the bootstrap snapshot. Leaving the form pristine is enough — Studio is the
    // source of truth after ``loadScene``; Quote line items persist via the project design PATCH.
  } catch (error) {
    console.warn('[syncQuoteSystemDesignIntoStudio] loadScene failed', error)
    const injected = injectQuoteOsSystemIntoEditor(projectData, systemUuid)
    if (!injected) {
      console.warn(
        '[syncQuoteSystemDesignIntoStudio] could not inject quote OsSystem — Quote/Payments widgets will use design snapshot fallback only'
      )
    }
  }
}

/**
 * Sleep-at-save guard: wait until Studio exposes the placeholder Quote system before serialising.
 * Runs ``syncQuoteSystemDesignIntoStudio`` when the node is missing, then polls for readiness.
 */
export async function ensureStudioHasQuoteSystem(params: {
  orgId: number | string
  projectId: string | number
  systemUuid: string
  maxWaitMs?: number
}): Promise<boolean> {
  const { projectId, systemUuid, maxWaitMs = 8000 } = params
  if (studioHasSystem(systemUuid)) {
    return true
  }

  const ready = await waitForStudioReady(maxWaitMs)
  if (!ready) return false

  const w = window as any
  if (!studioHasSystem(systemUuid) && w.editor && projectId && projectId !== 'new') {
    const { createQuotePlaceholderSystem } = await import('./createQuotePlaceholderSystem')
    createQuotePlaceholderSystem(projectId)
  }

  return studioHasSystem(systemUuid)
}
